import { useEffect, useRef } from "react";

/**
 * Trap keyboard focus inside the wrapped element while `active` is true.
 * Used by Modal / Drawer / Dialog primitives to prevent Tab from escaping.
 *
 * - Cycles Tab between focusable descendants.
 * - On activation, focuses the first focusable element (or the container).
 * - On deactivation, restores focus to the element that was focused before.
 * - Esc handling is not done here — Modal owns that.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function FocusTrap({
  active,
  children,
  initialFocusRef,
  className,
}: {
  active: boolean;
  children: React.ReactNode;
  initialFocusRef?: React.RefObject<HTMLElement>;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    previouslyFocused.current = document.activeElement as HTMLElement;
    const root = ref.current;
    if (!root) return;

    const target =
      initialFocusRef?.current ??
      (root.querySelector(FOCUSABLE) as HTMLElement | null) ??
      root;
    target.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key !== "Tab" || !root) return;
      const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter((el) => el.offsetParent !== null);
      if (items.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previouslyFocused.current?.focus?.();
    };
  }, [active, initialFocusRef]);

  return (
    <div ref={ref} tabIndex={-1} className={className}>
      {children}
    </div>
  );
}
